// Central content data for Lawyah — all page copy lives here

export const BRAND = "Lawyah";

export const NAV_LINKS = [
  { label: "How it works", path: "/how-it-works" },
  { label: "Marketing", path: "/marketing" },
  { label: "Pricing", path: "/pricing" },
  { label: "FAQ", path: "/faq" },
];

export const HERO = {
  eyebrow: "A layer on top of MyCase",
  title: ["Never miss a lead.", "Always know", "what it's worth."],
  subtitle:
    "Lawyah wraps around the practice-management system you already run. Our AI agent answers every call and chat around the clock, a live dashboard shows revenue by channel, and every lead syncs straight back into MyCase — nothing to migrate, nothing to relearn.",
  stats: [
    { value: "24/7", label: "phone and chat intake, including after hours" },
    { value: "6", label: "marketing channels synced into one dashboard" },
  ],
};

export const LOGO_STRIP = {
  text: "Runs on top of the systems litigation, family law, and personal injury firms already pay for",
  logos: ["MyCase", "Google LSA", "Google Business Profile", "Google Ads", "Meta Ads", "Dialpad"],
};

export const PROBLEMS = {
  eyebrow: "The problem",
  title: "Most firms lose cases before they ever reach a lawyer",
  items: [
    {
      title: "After-hours calls go to voicemail",
      body: "A prospective client calls at 9pm after a car accident or an arrest. If no one picks up, they call the next firm on the list — and that firm signs the case.",
    },
    {
      title: "Marketing spend spread across five different dashboards",
      body: "Google Ads, Local Services Ads, Meta, Google Business Profile, and MyCase all report their own numbers — but no one can say which channel actually produces paying, signed clients versus tire-kickers.",
    },
    {
      title: "Intake scattered across sticky notes and inboxes",
      body: "Names, case details, and conflict checks live across voicemail transcripts, a shared inbox, and whoever happened to answer the phone that day.",
    },
  ],
  fix: {
    eyebrow: "The fix",
    title: "One system that answers, qualifies, and proves it paid off",
    body: "Lawyah puts an AI intake specialist on every call and chat, runs the conflict check automatically, and drops a clean record straight into MyCase — with the marketing channel attached, every time.",
  },
};

export const STEPS = {
  eyebrow: "How it works",
  title: "From first ring to signed retainer",
  subtitle:
    "Four steps, running continuously, with no gaps for nights, weekends, or a front desk that's already on another line.",
  items: [
    {
      num: "1",
      title: "Lead calls or chats — any hour",
      body: "Lawyah picks up every inbound call and web chat instantly, day or night, in a natural conversational voice or text.",
    },
    {
      num: "2",
      title: "Lawyah captures details, runs conflict check",
      body: "Name, case type, opposing party, and key facts are gathered in a structured intake, and a conflict-of-interest check runs automatically.",
    },
    {
      num: "3",
      title: "Synced instantly to MyCase",
      body: "The finished intake, recording, and transcript land in your MyCase matter in seconds — no re-typing, no lost sticky notes.",
    },
    {
      num: "4",
      title: "You see attribution and follow up",
      body: "The dashboard shows which channel the lead came from and its likely value, so your team follows up on the leads worth chasing first.",
    },
  ],
};

export const PRODUCT_SECTIONS = {
  agent: {
    eyebrow: "Lawyah, the AI intake agent",
    title: "A front desk that never sleeps, never gets sick, and never forgets to ask about conflicts",
    body: "Lawyah handles phone and web chat intake with the same structured discipline every time — so no detail depends on who happened to be at the desk when the call came in.",
    bullets: [
      "24/7 phone intake with natural, voice-driven conversation",
      "Web chat intake matched to the same structured flow",
      "Automatic conflict-of-interest checks before a case is accepted",
      "Zero missed leads — every call is answered, every time",
    ],
  },
  dashboards: {
    eyebrow: "Dashboards & integrations",
    title: "One dashboard for every channel you already pay for",
    body: "MyCase, Google Local Services Ads, Google Business Profile, Google Ads, Meta, and your phone system all report into a single Lawyah view — no more logging into six different consoles to piece together what happened this week.",
    bullets: [
      "Native, two-way MyCase sync",
      "Google Local Services Ads (LSA) integration",
      "Google Business Profile, Google Ads & Meta, in the same view",
      "Phone-system integration for call-based leads",
    ],
    integrations: [
      { name: "MyCase", status: "Connected" },
      { name: "Google LSA", status: "Connected" },
      { name: "Google Business Profile", status: "Connected" },
      { name: "Google Ads", status: "Connected" },
      { name: "Meta Ads", status: "Connected" },
      { name: "Dialpad", status: "Connected" },
    ],
  },
  revenue: {
    eyebrow: "Revenue insights",
    title: "See revenue by channel — not just which one gets clicks",
    body: "Every lead carries its source through intake to signed retainer, so cost-per-lead becomes cost-per-client, and you can finally tell your marketing spend what it's actually worth.",
    bullets: [
      "Cost-per-lead and cost-per-signed-case, by channel",
      "UTM, campaign, and referral-source breakdowns",
      "Return on ad spend, calculated automatically",
      "Revenue reconciled against your own MyCase numbers",
    ],
    channels: [
      { name: "Google LSA", value: "$18.4k", pct: 100 },
      { name: "Google Ads", value: "$12.9k", pct: 70 },
      { name: "Meta Ads", value: "$9.6k", pct: 52 },
      { name: "Referrals", value: "$7.1k", pct: 39 },
    ],
    roas: { value: "4.6x", label: "return on LSA ad spend, this month" },
  },
  tracking: {
    eyebrow: "Lead tracking",
    title: "Watch every lead move, in real time",
    body: "A live funnel and a recent-leads feed give partners a daily read on pipeline health, from first contact through signed retainer — without waiting for an end-of-month report.",
    bullets: [
      "Enquiry → intake → paid consult → signed retainer",
      "Daily and weekly trend lines by practice area",
      "Recent leads feed, visible to the whole team",
    ],
    funnel: [
      { stage: "Enquiry", count: 248, pct: 100 },
      { stage: "Intake completed", count: 201, pct: 81 },
      { stage: "Paid consult", count: 96, pct: 39 },
      { stage: "Signed retainer", count: 58, pct: 23 },
    ],
    recent: [
      { name: "M. Okafor — auto accident", status: "Signed · 4m ago" },
      { name: "J. Park — custody modification", status: "Consult set · 22m ago" },
      { name: "R. Alvarez — slip and fall", status: "Intake · 41m ago" },
    ],
  },
  mycase: {
    eyebrow: "Works with MyCase",
    title: "Nothing to rip out, nothing to relearn",
    body: "Lawyah isn't another system your team has to learn from scratch — it wraps around your existing MyCase account. Matters, trust-ledger activity, and documents stay exactly where your team already looks for them.",
    bullets: [
      "Two-way sync with MyCase matters & intake fields",
      "Trust-ledger activity mirrored on an append-only log",
      "Keep your MyCase logins, billing, and client portal",
      "Go live without migrating a single case file",
    ],
    ledger: [
      { entry: "Retainer deposit", date: "Sep 2, 10:14 AM", matter: "Delgado v. Reyes", balance: "$5,000.00" },
      { entry: "Filing fee disbursed", date: "Sep 3, 2:41 PM", matter: "Delgado v. Reyes", balance: "$4,637.00" },
      { entry: "Retainer deposit", date: "Sep 4, 9:02 AM", matter: "Whitfield estate", balance: "$3,200.00" },
    ],
  },
  compliance: {
    eyebrow: "Compliance & security",
    title: "Built for the standard IOLTA compliance demands — not bolted on after",
    body: "A trust-accounting mistake can get a lawyer disbarred. Every account, matter, and dollar is isolated per firm, and every sync between Lawyah and MyCase is logged, permanently, with who did it and when.",
    bullets: [
      "Postgres row-level security — hard tenant isolation per firm",
      "Scope-based role permissions for every seat",
      "OAuth 2.0 authentication throughout",
      "Full, immutable audit trail on every money movement",
    ],
    audit: [
      "09:14:02 disbursement · $363.00 · Delgado v. Reyes · by A. Reyna",
      "09:41:57 retainer deposit · $3,200.00 · Whitfield estate · by system",
    ],
  },
};

export const MARKETING = {
  eyebrow: "Managed marketing",
  title: "We run your marketing — Google, Meta, open web, and OpenAI ads",
  subtitle:
    "Lawyah doesn't just measure your channels, we run them. Our team plans, launches, and optimizes campaigns across every major network — and every dollar of spend is tied back to signed retainers in your dashboard.",
  channels: [
    {
      name: "Google Ads & LSA",
      tag: "Search intent",
      body: "Search campaigns, Local Services Ads, and Google Business Profile — capturing clients at the exact moment they search for a lawyer.",
      points: ["Local Services Ads management", "Search & Performance Max campaigns", "Google Business Profile optimization"],
    },
    {
      name: "Meta Ads",
      tag: "Social reach",
      body: "Facebook and Instagram campaigns that keep your firm in front of your community — retargeting site visitors and past enquiries automatically.",
      points: ["Facebook & Instagram campaigns", "Retargeting & lookalike audiences", "Creative produced for legal compliance"],
    },
    {
      name: "Open Web & Programmatic",
      tag: "Display scale",
      body: "Programmatic display and native placements across the open web, tuned to your practice areas and geography — priced on performance, not impressions.",
      points: ["Programmatic display & native ads", "Geo-targeted to your service area", "Brand-safe, legal-vetted placements"],
    },
    {
      name: "OpenAI Ads",
      tag: "AI discovery",
      body: "As clients start asking AI assistants for lawyer recommendations, we place your firm inside AI-powered discovery surfaces — the newest channel, measured like every other one.",
      points: ["Placement in AI answer surfaces", "Early-mover advantage in AI search", "Same attribution, same dashboard"],
    },
  ],
  stats: [
    { value: "6", label: "ad networks managed under one roof" },
    { value: "100%", label: "of spend attributed to signed cases" },
    { value: "1", label: "dashboard for every channel" },
  ],
  how: {
    title: "Managed like an agency, measured like software",
    body: "Most agencies report clicks. We report signed retainers. Because Lawyah handles your intake, every campaign is graded on the only number that matters — revenue.",
    points: [
      "Dedicated campaign manager for your firm",
      "Monthly strategy reviews driven by revenue data",
      "Budgets shift automatically toward channels that sign cases",
      "No long-term lock-in — performance keeps you, not a contract",
    ],
  },
};

export const PRICING = {
  eyebrow: "Pricing",
  title: "Straightforward plans, sized to your firm",
  subtitle:
    "Every plan includes 24/7 intake and MyCase sync. We price per firm, not per feature you have to unlock later — talk to us and we'll size it to your call volume.",
  plans: [
    {
      name: "Starter",
      desc: "Solo practitioners and small teams getting off spreadsheets",
      price: "Custom",
      period: "/ month",
      cta: "Talk to us",
      featured: false,
      features: [
        "24/7 phone & chat intake with Lawyah",
        "Conflict checks & structured intake",
        "MyCase sync & document mirroring",
        "Up to 3 team seats",
      ],
    },
    {
      name: "Growth",
      desc: "Established firms that need attribution and trust accounting",
      price: "Custom",
      period: "/ month",
      cta: "Book a demo",
      featured: true,
      badge: "Most common",
      features: [
        "Everything in Starter",
        "IOLTA trust accounting & append-only ledger",
        "Marketing dashboards & integrations (LSA, GBP, Google Ads, Meta)",
        "Unlimited team seats",
      ],
    },
    {
      name: "Enterprise",
      desc: "Multi-office firms with dedicated compliance needs",
      price: "Custom",
      period: "/ month",
      cta: "Talk to us",
      featured: false,
      features: [
        "Everything in Growth",
        "Multi-office & multi-entity support",
        "Dedicated compliance & onboarding support",
        "Custom SLAs & data residency options",
      ],
    },
  ],
};

export const DEMO = {
  eyebrow: "Book a demo",
  title: "See Lawyah on your own leads",
  subtitle:
    "Twenty minutes, on a screen share, with your actual call types and case mix — not a generic slide deck.",
  points: [
    "We'll walk through a live intake call end to end",
    "We'll map your current channels into the revenue dashboard",
    "No pressure, no obligation, no credit card",
  ],
};

export const FAQS = {
  eyebrow: "FAQ",
  title: "Questions firms ask before switching",
  subtitle: "Don't see yours here — ask us directly on a demo call.",
  items: [
    {
      q: "Where does our data live, and how secure is it?",
      a: "Your firm's data is isolated with Postgres row-level security, so no other firm's rows are ever reachable through the application, even under a bug. Access uses OAuth 2.0 and scope-based permissions, and all data is encrypted in transit and at rest.",
    },
    {
      q: "How does Lawyah handle IOLTA compliance?",
      a: "Every trust-ledger entry is mirrored on an append-only log, timestamped, and attributed to the person or system that made it. Nothing can be edited after posting, so your audit trail is complete by construction — not reconstructed at audit time.",
    },
    {
      q: "Do we have to leave MyCase?",
      a: "No. Lawyah wraps around your existing MyCase account with a native two-way sync. Your matters, billing, logins, and client portal stay exactly where they are — nothing to migrate.",
    },
    {
      q: "What happens to our existing intake staff?",
      a: "Lawyah handles the volume no human team can — nights, weekends, and simultaneous calls. Your staff move up the value chain: reviewing qualified intakes, scheduling consults, and following up on the leads worth chasing first.",
    },
    {
      q: "Who manages our ad campaigns?",
      a: "We do. Our team runs your Google, Meta, open web, and OpenAI ad campaigns end to end, with a dedicated campaign manager and monthly strategy reviews — and every dollar is attributed to signed cases in your dashboard.",
    },
    {
      q: "How is Lawyah priced?",
      a: "Per firm, sized to your call volume and channel mix — not per feature. Every plan includes 24/7 intake and MyCase sync. Book a demo and we'll quote your exact configuration.",
    },
    {
      q: "How long does setup take?",
      a: "Most firms go live within a week. Because Lawyah wraps around MyCase rather than replacing it, there's no data migration — we connect your channels, configure your intake flow, and you're answering every call.",
    },
  ],
};

export const FOOTER = {
  blurb: "The AI intake and revenue layer for law firms. Answers every lead, proves every dollar.",
  contact: {
    email: "info@stratavaglobal.com",
    phone: "+1 (555) 210-4400",
    address: "By appointment — serving firms nationwide",
  },
  columns: [
    {
      title: "Explore",
      links: [
        { label: "How it works", path: "/how-it-works" },
        { label: "Marketing", path: "/marketing" },
        { label: "Pricing", path: "/pricing" },
      ],
    },
    {
      title: "Company",
      links: [
        { label: "Book a demo", path: "/book-demo" },
        { label: "FAQ", path: "/faq" },
        { label: "Lead inbox", path: "/admin" },
      ],
    },
  ],
  legal: [
    { label: "Privacy Policy", path: "/privacy" },
    { label: "Terms of Service", path: "/terms" },
  ],
};

export const UI = {
  nav: { login: "Log in", bookDemo: "Book a demo" },
  home: {
    bookDemo: "Book a demo",
    seeHow: "See how it works",
    fullWalkthrough: "See the full walkthrough",
    exploreMarketing: "Explore managed marketing",
    ctaTitle: "Every call answered. Every dollar accounted for.",
    ctaText: "See Lawyah on your own leads — twenty minutes, no slide deck, no obligation.",
    viewPricing: "View pricing",
  },
  hiw: {
    step: "Step",
    ctaTitle: "No gaps. No nights off. No leads lost.",
    ctaText: "The whole loop runs continuously — while your competitors' calls go to voicemail, yours go to Lawyah.",
  },
  marketing: {
    fullService: "Full-service",
    talkTeam: "Talk to our marketing team",
    attributionTitle: "Spend → signed cases, this month",
    signed: "signed",
    signedSummary: "31 signed retainers",
    signedSummaryLabel: "attributed across all managed channels",
  },
  pricing: {
    note: "Every plan includes 24/7 intake, MyCase sync, and onboarding support. Managed marketing is available as an add-on to any plan.",
  },
  demo: {
    formTitle: "Request a demo",
    formSubtitle: "We reply within one business day.",
    labels: { full_name: "Full name", firm_name: "Firm name", email: "Work email", phone: "Phone" },
    submit: "Request my demo",
    submitting: "Submitting…",
    disclaimer: "By submitting, you agree to be contacted about Lawyah. No spam, ever.",
    successTitle: "Request received",
    successText: "Thanks, {name}. We'll reply within one business day to schedule your demo.",
    submitAnother: "Submit another request",
    whatTitle: "What you'll see",
    whatItems: [
      "Lawyah answering a live intake call, end to end",
      "Your channels mapped into the revenue dashboard",
      "The managed marketing plan for your practice areas",
    ],
    toastSuccess: "Demo request received — we'll reply within one business day.",
    toastError: "Something went wrong. Please try again.",
    toastValidation: "Please fill in your name and work email.",
  },
  faq: {
    stillTitle: "Still have a question?",
    stillText: "Ask us directly — or ask Lawyah in the chat, any time.",
    stillCta: "Book a demo call",
  },
  footer: { contact: "Contact", rights: "All rights reserved." },
  chat: {
    title: "Lawyah — AI intake",
    online: "Online · replies instantly",
    welcome:
      "Hi, I'm Lawyah — the AI intake agent. I can tell you how we answer every call 24/7, sync leads to MyCase, and run your marketing across Google, Meta, open web, and OpenAI ads. What would you like to know?",
    placeholder: "Ask about intake, pricing, marketing…",
    error: "Sorry, I ran into a connection issue. Please try again in a moment.",
  },
};

export const LEGAL = {
  contactLabel: "Questions? Reach us at",
  privacy: {
    eyebrow: "Legal",
    title: "Privacy Policy",
    subtitle: "How Lawyah collects, uses, and protects your information.",
    updated: "Last updated: July 2025",
    sections: [
      {
        title: "Information we collect",
        body: "When you request a demo or chat with our AI assistant, we collect the details you choose to share — such as your name, firm name, work email, phone number, and the contents of your conversation.",
      },
      {
        title: "How we use it",
        body: "We use this information to respond to your enquiry, schedule demos, and improve our product. We do not sell your personal information to third parties.",
      },
      {
        title: "Data security",
        body: "Data is encrypted in transit and at rest. Firm data is isolated per tenant, and access is controlled with scope-based permissions and OAuth 2.0 authentication.",
      },
      {
        title: "Your choices",
        body: "You may request access to, correction of, or deletion of your personal information at any time by emailing us.",
      },
    ],
  },
  terms: {
    eyebrow: "Legal",
    title: "Terms of Service",
    subtitle: "The terms that govern your use of the Lawyah website and services.",
    updated: "Last updated: July 2025",
    sections: [
      {
        title: "Acceptance of terms",
        body: "By accessing this website or using Lawyah services, you agree to these terms. If you are using Lawyah on behalf of a firm, you represent that you are authorized to bind that firm.",
      },
      {
        title: "Use of the service",
        body: "Lawyah provides AI-powered intake, dashboards, and managed marketing for law firms. You agree to use the service lawfully and not to attempt to access data belonging to other firms.",
      },
      {
        title: "No legal advice",
        body: "Lawyah is a software and marketing service. Nothing on this website or in our AI assistant's responses constitutes legal advice.",
      },
      {
        title: "Limitation of liability",
        body: "To the maximum extent permitted by law, Lawyah is not liable for indirect, incidental, or consequential damages arising from use of the service.",
      },
    ],
  },
};
